import { createInertiaApp, router } from "@inertiajs/vue3";
import ElementPlus from "element-plus";
import "element-plus/dist/index.css";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { createApp, h } from "vue";
import { ZiggyVue } from "../../vendor/tightenco/ziggy/dist/vue.m";
import "../css/app.css";
import "./bootstrap";
import { initGlobalModalBackdrop } from "./composables/useModalBackdrop";
import useSubscriptionStatus from "./composables/useSubscriptionStatus";
import * as helpers from "./utils/helpers";
import { hasTrackingConsent } from "./utils/trackingConsent";

import "../../public/template/allmyonline/assets/css/bootstrap.min.css";
import "../../public/template/allmyonline/assets/css/common.css";
//Import vue-recaptcha-v3
import Clarity from "@microsoft/clarity";
import * as Sentry from "@sentry/vue";
import { VueReCaptcha } from "vue-recaptcha-v3";
import Vue3Toastify from "vue3-toastify";
import "vue3-toastify/dist/index.css";

const appName =
    window.document.getElementsByTagName("title")[0]?.innerText || "Laravel";

const TRACKING_SESSION_UUID_STORAGE_KEY = "amo_session_uuid";

const normalizeTrackingValue = (value) => {
    if (typeof value !== "string") {
        return "";
    }

    return value.trim();
};

const normalizeTrackingEmail = (value) =>
    normalizeTrackingValue(value).toLowerCase();

const buildClarityPageId = (page) => {
    const component = normalizeTrackingValue(page?.component);
    const fallbackUrl =
        typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search}`
            : "";
    const rawUrl = normalizeTrackingValue(page?.url) || fallbackUrl;

    try {
        const parsedUrl = new URL(
            rawUrl || fallbackUrl || "/",
            window.location.origin
        );
        return [component, parsedUrl.pathname]
            .filter(Boolean)
            .join(":")
            .slice(0, 255);
    } catch (_) {
        return (component || rawUrl || fallbackUrl).slice(0, 255);
    }
};

const resolveTrackingSessionUuid = (pageProps = {}) => {
    if (typeof window === "undefined") {
        return normalizeTrackingValue(pageProps?.session_uuid);
    }

    const backendSessionUuid = normalizeTrackingValue(pageProps?.session_uuid);
    const storedSessionUuid = normalizeTrackingValue(
        window.sessionStorage.getItem(TRACKING_SESSION_UUID_STORAGE_KEY)
    );

    let sessionUuid = storedSessionUuid || backendSessionUuid;

    if (!sessionUuid) {
        sessionUuid =
            window.crypto && window.crypto.randomUUID
                ? window.crypto.randomUUID()
                : Math.random().toString(36).slice(2) +
                  Date.now().toString(36);
    }

    try {
        window.sessionStorage.setItem(
            TRACKING_SESSION_UUID_STORAGE_KEY,
            sessionUuid
        );
    } catch (_) {}

    return sessionUuid;
};

const syncUserTracking = ({ page, clarityEnabled, appEnv }) => {
    if (typeof window === "undefined") {
        return;
    }

    const pageProps = page?.props ?? {};
    const sessionUuid = resolveTrackingSessionUuid(pageProps);
    const user = pageProps?.auth?.user ?? null;
    const userId = normalizeTrackingValue(user?.id ? String(user.id) : "");
    const userEmail = normalizeTrackingEmail(user?.email);
    const userName = normalizeTrackingValue(user?.name);
    const username = normalizeTrackingValue(user?.username);
    const userPlan = normalizeTrackingValue(user?.plan);
    const clarityCustomUserId = userEmail || (userId ? `user:${userId}` : "");
    const clarityFriendlyName = userName || username || undefined;
    const clarityPageId = buildClarityPageId(page);

    Sentry.setTag("session_uuid", sessionUuid);

    if (userId) {
        Sentry.setUser({
            id: userId,
            email: userEmail || undefined,
            username: username || undefined,
        });
    } else {
        Sentry.setUser(null);
    }

    if (!clarityEnabled || appEnv === "local") {
        return;
    }

    try {
        Clarity.setTag("session_uuid", sessionUuid);
    } catch (_) {}

    try {
        if (page?.component) {
            Clarity.setTag("page_component", String(page.component));
        }
    } catch (_) {}

    try {
        if (userId) {
            Clarity.setTag("user_id", userId);
        }
    } catch (_) {}

    try {
        if (username) {
            Clarity.setTag("username", username);
        }
    } catch (_) {}

    try {
        if (userPlan) {
            Clarity.setTag("plan", userPlan);
        }
    } catch (_) {}

    if (!clarityCustomUserId) {
        return;
    }

    try {
        Clarity.identify(
            clarityCustomUserId,
            sessionUuid || undefined,
            clarityPageId || undefined,
            clarityFriendlyName
        );
    } catch (_) {}
};

const sendTrackingCookiesToBackend = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return;
    }

    const trackingSessionKey = "amo_tracking_cookies_sent";
    const hasSentTracking =
        window.sessionStorage.getItem(trackingSessionKey) === "true";

    if (hasSentTracking) {
        return;
    }

    const readCookie = (name) => {
        const value = document.cookie
            .split("; ")
            .find((row) => row.startsWith(`${name}=`));
        return value ? decodeURIComponent(value.split("=")[1]) : "";
    };

    const sendWithRetry = async (attempt = 0) => {
        const fbp = readCookie("_fbp");
        const fbc = readCookie("_fbc");
        const gaCookie = readCookie("_ga");
        let gaClientId = "";

        if (gaCookie) {
            const segments = gaCookie.split(".");
            if (segments.length >= 4) {
                gaClientId = segments.slice(-2).join(".");
            }
        }

        const hasCookies = fbp || fbc || gaClientId;

        if (!hasCookies && attempt < 3) {
            setTimeout(() => sendWithRetry(attempt + 1), 1000);
            return;
        }

        if (!hasCookies) {
            return;
        }

        const csrfToken =
            document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content") || "";

        if (!csrfToken) {
            return;
        }

        const payload = {
            facebook: {
                fbp: fbp || null,
                fbc: fbc || null,
            },
            google: {
                ga_cookie: gaCookie || null,
                client_id: gaClientId || null,
            },
        };

        try {
            const response = await fetch("/tracking/cookies", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-TOKEN": csrfToken,
                    "X-Requested-With": "XMLHttpRequest",
                },
                credentials: "same-origin",
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                window.sessionStorage.setItem(trackingSessionKey, "true");
            }
        } catch (_) {
            // no-op
        }
    };

    sendWithRetry();
};

// Initialize Inertia app with custom setup
createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent(
            `./Pages/${name}.vue`,
            import.meta.glob("./Pages/**/*.vue")
        ),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        const initialPage = props?.initialPage;
        const initialPageProps = initialPage?.props ?? {};

        // Detect screenshot mode from Inertia props (set by ProfileAccessBypassService)
        const isScreenshotMode = initialPageProps?.screenshotMode === true;

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_VUE_DSN,
            sendDefaultPii: true,
        });

        // Initialize Microsoft Clarity (browser only, not in local or screenshot mode)
        const clarityId = import.meta.env.VITE_MS_CLARITY_ID;
        const appEnv = import.meta.env.VITE_APP_ENV;
        const clarityEnabled =
            typeof window !== "undefined" &&
            Boolean(clarityId) &&
            appEnv !== "local" &&
            !isScreenshotMode;

        if (clarityEnabled) {
            try {
                Clarity.init(clarityId);
            } catch (e) {
                // no-op
            }

            if (hasTrackingConsent()) {
                try {
                    Clarity.consent();
                } catch (_) {}
            }
        }

        if (typeof window !== "undefined") {
            syncUserTracking({
                page: initialPage,
                clarityEnabled,
                appEnv,
            });

            router.on("navigate", (event) => {
                syncUserTracking({
                    page: event?.detail?.page,
                    clarityEnabled,
                    appEnv,
                });
            });

            if (!isScreenshotMode) {
                sendTrackingCookiesToBackend();
            }
        }

        // Make helpers available globally
        app.config.globalProperties.$helpers = helpers;

        // Make appUrl globally available
        app.config.globalProperties.$appUrl = props.initialPage.props.appUrl;

        // Use Inertia, Ziggy, and ElementPlus plugins
        app.use(plugin)
            .use(ZiggyVue, Ziggy) // Ziggy for route helpers
            .use(ElementPlus) // UI components from ElementPlus
            .use(Vue3Toastify, {
                autoClose: 3000,
                position: "top-right",
                theme: "light",
            });

        // Only load reCAPTCHA on non-local environments
        if (props.initialPage.props.env !== 'local') {
            app.use(VueReCaptcha, {
                siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
                loaderOptions: {
                    autoHideBadge: true,
                },
            });
        }

        // Register subscription status as global properties (unwrap computed refs via getters)
        try {
            const {
                plan,
                accessStatus,
                isFree,
                isPro,
                isPremium,
                isRestricted,
            } = useSubscriptionStatus();

            Object.defineProperties(app.config.globalProperties, {
                $plan: { get: () => plan?.value },
                $accessStatus: { get: () => accessStatus?.value },
                $isFree: { get: () => isFree?.value === true },
                $isPro: { get: () => isPro?.value === true },
                $isPremium: { get: () => isPremium?.value === true },
                $isRestricted: { get: () => isRestricted?.value === true },
            });
        } catch (_) {}

        // Mount the app after initialization
        app.mount(el);

        // Initialize global modal backdrop manager
        initGlobalModalBackdrop();
    },
    progress: {
        color: "#4B5563", // Color of the loading bar
    },
});
